import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Radio, ArrowLeft, Calendar, MapPin, Building2, User, 
  Settings, CheckCircle, AlertTriangle, Clock, Battery, 
  Signal, Wifi, Bluetooth, QrCode, Printer, Activity
} from 'lucide-react';
import { apiService } from '../services/api';

interface RadioDetails {
  id: number;
  radio_name: string;
  brand: string;
  model: string;
  serial_number: string;
  inventory_number?: string;
  status: string;
  health_status: string;
  battery_level?: number;
  signal_strength?: number;
  communication_protocol: string;
  frequency?: string;
  channel?: string;
  location_id: number;
  company_id: number;
  assigned_user_id?: number;
  purchase_date?: string;
  warranty_end_date?: string;
  purchase_price?: number;
  currency: string;
  notes?: string;
  created_at: string;
  updated_at: string;
  location?: {
    name: string;
    address: string;
    phone: string;
  };
  company?: {
    name: string;
  };
  assigned_user?: {
    name: string;
    email: string;
  };
}

interface ServiceRecord {
  id: number;
  radio_id: number;
  service_date: string;
  service_type: string;
  description: string;
  technician: string;
  cost?: number;
  status: string;
  created_at: string;
}

const RadioDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [radio, setRadio] = useState<RadioDetails | null>(null);
  const [serviceHistory, setServiceHistory] = useState<ServiceRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (id) {
      loadRadioDetails();
    }
  }, [id]);

  const loadRadioDetails = async () => {
    try {
      setLoading(true);
      const [radioData, serviceData] = await Promise.all([
        apiService.getRadio(parseInt(id!)),
        apiService.getRadioServiceHistory(parseInt(id!))
      ]);
      
      setRadio(radioData);
      setServiceHistory(serviceData);
    } catch (error: any) {
      console.error('Error loading radio details:', error);
      setError('Telsiz bilgileri yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'inactive': return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200';
      case 'maintenance': return 'bg-yellow-100 text-yellow-800';
      case 'broken': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'active': return 'Aktif';
      case 'inactive': return 'Pasif';
      case 'maintenance': return 'Bakımda';
      case 'broken': return 'Arızalı';
      default: return status;
    }
  };

  const getHealthStatusColor = (health: string) => {
    switch (health) {
      case 'excellent': return 'bg-green-100 text-green-800';
      case 'good': return 'bg-primary/20 text-primary-dark';
      case 'fair': return 'bg-yellow-100 text-yellow-800';
      case 'poor': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200';
    }
  };

  const getHealthStatusLabel = (health: string) => {
    switch (health) {
      case 'excellent': return 'Mükemmel';
      case 'good': return 'İyi';
      case 'fair': return 'Orta';
      case 'poor': return 'Kötü';
      default: return health;
    }
  };

  const getProtocolIcon = (protocol: string) => {
    switch (protocol) {
      case 'wifi': return Wifi;
      case 'bluetooth': return Bluetooth;
      default: return Radio;
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'Belirtilmemiş';
    return new Date(dateString).toLocaleDateString('tr-TR');
  };

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: currency === 'TRY' ? 'TRY' : 'USD'
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-700 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Telsiz bilgileri yükleniyor...</p>
        </div>
      </div>
    );
  }

  if (error || !radio) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-700 flex items-center justify-center">
        <div className="text-center">
          <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">Hata</h2>
          <p className="text-gray-600 dark:text-gray-400 mb-4">{error || 'Telsiz bulunamadı'}</p>
          <button
            onClick={() => navigate('/')}
            className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary-dark transition-colors"
          >
            Ana Sayfaya Dön
          </button>
        </div>
      </div>
    );
  }

  const ProtocolIcon = getProtocolIcon(radio.communication_protocol);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-700">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <button
                onClick={() => navigate('/')}
                className="mr-4 p-2 text-gray-400 hover:text-gray-600 dark:text-gray-400 transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="flex items-center">
                <div className="w-10 h-10 bg-primary/20 rounded-lg flex items-center justify-center mr-3">
                  <Radio className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">{radio.radio_name}</h1>
                  <p className="text-sm text-gray-600 dark:text-gray-400">{radio.brand} {radio.model}</p>
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <span className={`px-3 py-1 text-sm font-medium rounded-full ${getStatusColor(radio.status)}`}>
                {getStatusLabel(radio.status)}
              </span>
              <span className={`px-3 py-1 text-sm font-medium rounded-full ${getHealthStatusColor(radio.health_status)}`}>
                {getHealthStatusLabel(radio.health_status)}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Sol Kolon - Telsiz Bilgileri */}
          <div className="lg:col-span-2 space-y-6">
            {/* Temel Bilgiler */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border">
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Telsiz Bilgileri</h2>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-3">Temel Bilgiler</h3>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Telsiz Adı:</span>
                        <span className="font-medium">{radio.radio_name}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Marka/Model:</span>
                        <span className="font-medium">{radio.brand} {radio.model}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Seri Numarası:</span>
                        <span className="font-medium">{radio.serial_number}</span>
                      </div>
                      {radio.inventory_number && (
                        <div className="flex justify-between">
                          <span className="text-gray-600 dark:text-gray-400">Envanter No:</span>
                          <span className="font-medium">{radio.inventory_number}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div>
                    <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-3">Teknik Özellikler</h3>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Protokol:</span>
                        <div className="flex items-center">
                          <ProtocolIcon className="w-4 h-4 mr-1" />
                          <span className="font-medium capitalize">{radio.communication_protocol}</span>
                        </div>
                      </div>
                      {radio.frequency && (
                        <div className="flex justify-between">
                          <span className="text-gray-600 dark:text-gray-400">Frekans:</span>
                          <span className="font-medium">{radio.frequency}</span>
                        </div>
                      )}
                      {radio.channel && (
                        <div className="flex justify-between">
                          <span className="text-gray-600 dark:text-gray-400">Kanal:</span>
                          <span className="font-medium">{radio.channel}</span>
                        </div>
                      )}
                      {radio.battery_level !== null && (
                        <div className="flex justify-between">
                          <span className="text-gray-600 dark:text-gray-400">Batarya:</span>
                          <div className="flex items-center">
                            <Battery className="w-4 h-4 mr-1" />
                            <span className="font-medium">%{radio.battery_level}</span>
                          </div>
                        </div>
                      )}
                      {radio.signal_strength !== null && (
                        <div className="flex justify-between">
                          <span className="text-gray-600 dark:text-gray-400">Sinyal:</span>
                          <div className="flex items-center">
                            <Signal className="w-4 h-4 mr-1" />
                            <span className="font-medium">{radio.signal_strength} dBm</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Lokasyon ve Atama */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border">
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Lokasyon ve Atama</h2>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="flex items-center">
                    <Building2 className="w-5 h-5 text-gray-400 mr-3" />
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Şirket</p>
                      <p className="font-medium">{radio.company?.name || 'Belirtilmemiş'}</p>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <MapPin className="w-5 h-5 text-gray-400 mr-3" />
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Lokasyon</p>
                      <p className="font-medium">{radio.location?.name || 'Belirtilmemiş'}</p>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <User className="w-5 h-5 text-gray-400 mr-3" />
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Atanan Kullanıcı</p>
                      <p className="font-medium">{radio.assigned_user?.name || 'Atanmamış'}</p>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <Calendar className="w-5 h-5 text-gray-400 mr-3" />
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Satın Alma Tarihi</p>
                      <p className="font-medium">{formatDate(radio.purchase_date || '')}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Notlar */}
            {radio.notes && (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border">
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Notlar</h2>
                </div>
                <div className="p-6">
                  <p className="text-gray-700 dark:text-gray-300">{radio.notes}</p>
                </div>
              </div>
            )}
          </div>

          {/* Sağ Kolon - Servis Geçmişi */}
          <div className="space-y-6">
            {/* Durum Bilgileri */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border">
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Durum Bilgileri</h2>
              </div>
              <div className="p-6 space-y-4">
                <div className="flex items-center">
                  <Activity className="w-5 h-5 text-primary mr-3" />
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Durum</p>
                    <p className="font-medium">{getStatusLabel(radio.status)}</p>
                  </div>
                </div>
                
                <div className="flex items-center">
                  <Settings className="w-5 h-5 text-green-500 mr-3" />
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Sağlık Durumu</p>
                    <p className="font-medium">{getHealthStatusLabel(radio.health_status)}</p>
                  </div>
                </div>

                {radio.warranty_end_date && (
                  <div className="flex items-center">
                    <Calendar className="w-5 h-5 text-orange-500 mr-3" />
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Garanti Bitiş</p>
                      <p className="font-medium">{formatDate(radio.warranty_end_date)}</p>
                    </div>
                  </div>
                )}

                {radio.purchase_price && (
                  <div className="flex items-center">
                    <span className="w-5 h-5 text-green-500 mr-3">₺</span>
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Satın Alma Fiyatı</p>
                      <p className="font-medium">{formatCurrency(radio.purchase_price, radio.currency)}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Servis Geçmişi */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border">
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Servis Geçmişi</h2>
              </div>
              <div className="p-6">
                {serviceHistory.length === 0 ? (
                  <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                    <Settings className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                    <p>Henüz servis kaydı bulunmuyor</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {serviceHistory.slice(0, 5).map((record) => (
                      <div key={record.id} className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                        <div className="flex justify-between items-start mb-2">
                          <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                            {formatDate(record.service_date)}
                          </div>
                          <div className={`text-xs px-2 py-1 rounded-full ${
                            record.status === 'completed' ? 'bg-green-100 text-green-800' :
                            record.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'
                          }`}>
                            {record.status === 'completed' ? 'Tamamlandı' :
                             record.status === 'pending' ? 'Beklemede' : record.status}
                          </div>
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                          <p><strong>Tip:</strong> {record.service_type}</p>
                          <p><strong>Teknisyen:</strong> {record.technician}</p>
                          <p><strong>Açıklama:</strong> {record.description}</p>
                          {record.cost && <p><strong>Maliyet:</strong> {formatCurrency(record.cost, 'TRY')}</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* QR Kod Bilgisi */}
            <div className="bg-primary/10 border border-primary/30 rounded-lg p-4">
              <div className="flex items-center mb-2">
                <QrCode className="w-5 h-5 text-primary mr-2" />
                <h3 className="text-sm font-medium text-primary-dark">QR Kod Bilgisi</h3>
              </div>
              <p className="text-xs text-primary-dark">
                Bu QR kod okutularak telsiz bilgilerine ve servis geçmişine erişebilirsiniz.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RadioDetailsPage;
